import { forwardRef, useState, useEffect } from "react";
import Modal from "shared/Components/modal";
import { globalVariables } from "helper/globalVariables";
import { useForm } from "react-hook-form";
import { TextInput } from "shared/Components/textInput";
import { FileUpload } from "shared/Components/fileUpload";
import Dropdown from "shared/Components/dropdown";
import { CheckBox } from "shared/Components/checkbox";
import { dog } from "helper/images";
import CustomDateOfBirthPicker from "shared/Components/dateOfBirthPicker";
import { useUploadImage } from "redux/hooks";
import { TextButton } from "shared/Components/textButton";
import { breedDropdown } from "container/OnBoarding/RegisterDog/Constants/breeds";
import { client } from "helper/apolloConfig";
import {
  updateRegisteredDogs,
} from "redux/features/dogSlice/dogGql";

export const EditDogCard = forwardRef((props, ref) => {
  const { maxHeight = "max-h-[480px]", dogInfo, setIsDataUpdated } = props || {};
  const { onUploadImage, imageUrl } = useUploadImage();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm();
  const genderDropdown = [
    "Male Intact",
    "Male Neutered",
    "Female Intact",
    "Female Spayed",
  ];
  const [showBreedMixInput, setShowBreedMixInput] = useState(false);
  useEffect(() => {
    if (dogInfo) {
      setValue("call_name", dogInfo.dog.call_name || "");
      setValue("registration_name", dogInfo.dog.registration_name || "");
      setValue("date_of_birth", dogInfo.dog.date_of_birth || "");
      setValue("breed", dogInfo.dog.breed || "");
      setValue("breed_mix", dogInfo.dog.breed_mix || "");
      setValue("gender", dogInfo.dog.gender || "");
      setValue("rescue", dogInfo.dog.rescue || false);
      const isMixedBreed = ["Mixed Breed", "Mixed Breed - Designer Dogs", "Other"].includes(dogInfo.dog.breed);
      setShowBreedMixInput(isMixedBreed);
    }
  }, [dogInfo]);

  const dogImage = imageUrl ? imageUrl : dogInfo?.dog.dogImage;

  const onHandleSubmit = async (data) => {
    const id = dogInfo?.dog.id;
    const updatedData = {
      ...data,
      dogImage,
      id,
    };

    try {
      const dataUpdateResult = await client.mutate({
        mutation: updateRegisteredDogs,
        variables: { data: [updatedData]}, // Wrap in array as mutation expects array
      });
      if (dataUpdateResult?.data) {
        setIsDataUpdated(true);
        // Force page reload to ensure all data is refreshed
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (error) {
      console.error(error);
    }
  };
  
  return (
    <Modal
      extraClass={`${maxHeight} pt-0`}
      headerClass="sticky top-6 left-100 z-50"
      iconPosition="right-0"
      ref={ref}
    >
      <div className="pt-4">
        <FileUpload
          icon={dogInfo?.dog.dogImage || dog}
          width={"w-[100%] md:w-[350px] lg:w-[350px]"}
          onSetImage={(img) => onUploadImage(img)}
          defaultImage={dogInfo?.dogImage}
        />
        <div className="mt-2" />
        <form onSubmit={handleSubmit(onHandleSubmit)}>
          <TextInput
            label={globalVariables.enterCallName}
            name="call_name"
            placeholder={globalVariables.enterCallName}
            register={register}
          />
          <div className="mt-2" />
          <TextInput
            label={globalVariables.enterRegisteredName}
            name="registration_name"
            placeholder={globalVariables.enterRegisteredName}
            maxLength={100}
            register={register}
            errorMsg={errors.registration_name?.message}
          />
          <div className="mt-2" />
          <CustomDateOfBirthPicker
            label={globalVariables.dateOfBirth}
            onSetDate={(date) => setValue("date_of_birth", date)}
            defaultDate={new Date(watch("date_of_birth"))}
            width="w-[100%]"
            placeholder={globalVariables.dateOfBirth}
            maxDate={new Date()}
          />
          <div className="mt-2" />
          <Dropdown
            label={globalVariables.breed}
            dropdownOptions={breedDropdown}
            placeholder={globalVariables.breed}
            defaultOption={watch("breed")}
            onChange={(value) => {
              setValue("breed", value);
              const isMixedBreed = ["Mixed Breed", "Mixed Breed - Designer Dogs", "Other"].includes(value);
              setShowBreedMixInput(isMixedBreed);
            }}
            width="w-[100%]"
            errorMsg={errors.breed?.message}
          />
          {showBreedMixInput && (
            <>
              <div className="mt-2" />
              <TextInput
                name="breed_mix"
                placeholder={globalVariables.breedMix}
                register={register}
                errorMsg={errors.breed_mix?.message}
              />
            </>
          )}
          <div className="mt-2" />
          <Dropdown
            label={globalVariables.gender}
            dropdownOptions={genderDropdown}
            placeholder={globalVariables.gender}
            defaultOption={watch("gender")}
            onChange={(value) => setValue("gender", value)}
            width="w-[100%]"
            errorMsg={errors.gender?.message}
          />
          <div className="mt-2" />
          <div className="flex flex-row justify-between">
            <span className="text-base font-medium text-gray-700">
              {globalVariables.rescue}
            </span>
            <div className="flex flex-row">
              <CheckBox
                label={globalVariables.yes}
                isChecked={watch("rescue")}
                onChange={() => setValue("rescue", true)}
              />
              <div className="mr-4" />
              <CheckBox
                label={globalVariables.no}
                isChecked={!watch("rescue")}
                onChange={() => setValue("rescue", false)}
              />
            </div>
          </div>
          <div className="mt-5 flex justify-end">
            <TextButton type="submit" label={globalVariables.save} />
          </div>
        </form>
      </div>
    </Modal>
  );
});
